import { useEffect, useState } from "react";
import {
  Messages,
  NotificationSeverity,
  TABLE_ACTIONS,
} from "helpers/constants";
import ConfirmActionModal from "modals/ConfirmActionModal/ConfirmActionModal";
import CustomTable from "components/Table/CustomTable";
import { Resource } from "types/resource";
import {
  useDeleteResourceMutation,
  useGetResourcesQuery,
} from "graphql/__generated__/hooks";
import CreateOrEditResourceModal from "modals/CreateOrEditResourceModal/CreateOrEditResourceModal";
import { MainLayoutContainer } from "components/Layout";
import { Add } from "@mui/icons-material";
import { Box, Link as LinkBase, Stack, Tooltip } from "@mui/material";
import { ellipsizeUrl } from "utils/urls";
import { ResponsiveButton } from "components/Button";
import { Link, useNavigate } from "react-router-dom";
import { useNotifications } from "hooks/useNotifications";

export default function ResourcesList() {
  const navigate = useNavigate();
  const [createResourceModal, setCreateResourceModal] = useState(false);
  const [deleteResourceModal, setDeleteResourceModal] = useState(false);
  const [resourceIdToDelete, setResourceIdToDelete] = useState("");
  const [resources, setResources] = useState([] as Resource[]);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(
    null,
  );
  const [deleteResource] = useDeleteResourceMutation();
  const { loading, error, data, refetch } = useGetResourcesQuery();
  const { notify } = useNotifications();

  useEffect(() => {
    setResources((data?.resource as Resource[]) || []);
  }, [data]);

  const handleActionClick = (action: TABLE_ACTIONS, id: string) => {
    switch (action) {
      case TABLE_ACTIONS.VIEW:
        navigate(`/resource/${id}`);
        break;
      case TABLE_ACTIONS.EDIT:
        const resourceToEdit = resources?.find(
          (resource) => resource.resource_id === id,
        );
        if (resourceToEdit) {
          setSelectedResource(resourceToEdit);
          setCreateResourceModal(true);
        }
        break;
      case TABLE_ACTIONS.DELETE:
        setResourceIdToDelete(id);
        setDeleteResourceModal(true);
        break;
      default:
        break;
    }
  };

  const handleCreateResourceModalOpen = () => {
    setCreateResourceModal(true);
  };

  const handleCreateOrEditResourceChange = () => {
    if (selectedResource !== null) {
      setSelectedResource(null);
    }
    setCreateResourceModal(false);
    refetch().then();
  };

  const handleDelete = async () => {
    if (!resourceIdToDelete || resourceIdToDelete === "") {
      return;
    }
    await deleteResource({ variables: { id: resourceIdToDelete } });
    await refetch();
    setResourceIdToDelete("");
    notify({
      message: Messages.successResourceDeleted,
      severity: NotificationSeverity.success,
    });
  };

  return (
    <MainLayoutContainer
      title="Resources"
      loading={loading}
      error={error}
      buttonActions={
        <ResponsiveButton
          onClick={() => handleCreateResourceModalOpen()}
          icon={<Add />}
        >
          New Resource
        </ResponsiveButton>
      }
    >
      <CustomTable
        entities={resources}
        handleActionClick={handleActionClick}
        fieldToFilter={"name"}
        columns={[
          {
            id: "name",
            label: "Name",
            value: "name",
          },
          {
            id: "description",
            label: "Description",
            value: (row: any) => {
              return row.description.length > 128
                ? row.description.slice(0, 128)
                : row.description;
            },
          },
          {
            id: "url",
            label: "Url",
            value: (row: any) => {
              return (
                <Tooltip title={row.url} arrow>
                  <Box>{ellipsizeUrl(row.url || "", 32)}</Box>
                </Tooltip>
              );
            },
          },
          {
            id: "resource_id",
            value: (row: any) => (
              <Stack direction="row" spacing={2} alignItems={"center"}>
                <LinkBase
                  sx={{ cursor: "pointer" }}
                  component={Link}
                  to={`/resource/${row.resource_id}`}
                >
                  View
                </LinkBase>
              </Stack>
            ),
          },
          { id: "actions", value: "" },
        ]}
        isSelectable={false}
        allowedMenuItems={[
          TABLE_ACTIONS.VIEW,
          TABLE_ACTIONS.EDIT,
          TABLE_ACTIONS.DELETE,
        ]}
        extraActionItems={[]}
        idColumn={"resource_id"}
      />

      {createResourceModal && (
        <CreateOrEditResourceModal
          handleCreateOrEditResourceChange={handleCreateOrEditResourceChange}
          createOrEditResourceModal={createResourceModal}
          handleCreateOrEditResourceModalDismiss={() => {
            setCreateResourceModal(false);
            if (selectedResource !== null) {
              setSelectedResource(null);
            }
          }}
          resource={selectedResource}
        />
      )}

      {deleteResourceModal && (
        <ConfirmActionModal
          title={"Heads up!"}
          message={"Are you sure you want to delete this resource?"}
          show={deleteResourceModal}
          onConfirm={handleDelete}
          onDismiss={() => {
            setDeleteResourceModal(false);
          }}
        />
      )}
    </MainLayoutContainer>
  );
}
